import { legacyManagementCenterMultiGridKey, localStorageService, managementCenterMultiGridKey } from "../../../services/LocalStorageService";
import { metricGroupForField } from "../../OpsBackoffice/metricColumnGroups";
import { isStringArray, normalizeBandContiguousOrder, renameLegacyGridFields, sanitizeLegacySortState } from "./utils";

// The stored value is parsed JSON typed only by the reader, so it is checked before it is regrouped; a
// malformed one is dropped, leaving the grid on its natural order.
const migrateOrder = (userId: string): void => {
	const legacy: unknown = localStorageService.getTableColumnOrder(legacyManagementCenterMultiGridKey, userId);
	if (legacy === null) return;
	if (!isStringArray(legacy)) {
		console.warn("Discarding a malformed saved multi-facility grid column order", legacy);
	} else if (!localStorageService.getTableColumnOrder(managementCenterMultiGridKey, userId)) {
		const migrated = normalizeBandContiguousOrder(renameLegacyGridFields(legacy), metricGroupForField);
		localStorageService.saveTableColumnOrder(managementCenterMultiGridKey, migrated, userId);
	}
	localStorageService.removeTableColumnOrder(legacyManagementCenterMultiGridKey, userId);
};

// Per-user reads only: the sort is also mirrored to a raw key shared by everyone on the browser, so the
// fallback reader would let one user's migrated sort mark another user as done (skipping, then deleting,
// that user's own legacy sort) — or hand them someone else's.
const migrateSort = (userId: string): void => {
	const legacy: unknown = localStorageService.getOwnTableSortingState<Record<string, unknown>>(legacyManagementCenterMultiGridKey, userId);
	if (legacy === null) return;
	if (!Array.isArray(legacy)) {
		console.warn("Discarding a malformed saved multi-facility grid sort", legacy);
	} else if (!localStorageService.getOwnTableSortingState(managementCenterMultiGridKey, userId)) {
		localStorageService.saveTableSortingState(managementCenterMultiGridKey, sanitizeLegacySortState(legacy), userId);
	}
	localStorageService.removeTableSortingState(legacyManagementCenterMultiGridKey, userId);
};

const migrateWidths = (userId: string): void => {
	const legacy = localStorageService.getTableColumnWidths(legacyManagementCenterMultiGridKey, userId);
	if (!legacy) return;
	if (!localStorageService.getTableColumnWidths(managementCenterMultiGridKey, userId)) {
		localStorageService.saveTableColumnWidths(managementCenterMultiGridKey, legacy, userId);
	}
	localStorageService.removeTableColumnWidths(legacyManagementCenterMultiGridKey, userId);
};

/**
 * One-time carry-over of a user's grid layout from the previous storage key: drag order (regrouped so
 * every header band is one run, renamed/retired fields fixed up), sort (retired axes dropped, renamed ones
 * re-pointed) and column widths as-is. Visibility is deliberately NOT carried over — the old saved model
 * holds the old defaults as explicit `false`s, which would keep hiding columns that are now shown by
 * default — so everyone starts from the current default set. Each legacy entry is removed once read, so
 * after the first run this is a handful of empty reads. A piece already present under the new key is
 * never overwritten. No user id yet means nothing to migrate.
 */
export const migrateLegacyMultiFacilityGridStorage = (userId: string): void => {
	if (!userId) return;
	migrateOrder(userId);
	migrateSort(userId);
	migrateWidths(userId);
	if (localStorageService.getTableColumnVisibility(legacyManagementCenterMultiGridKey, userId)) {
		localStorageService.removeTableColumnVisibility(legacyManagementCenterMultiGridKey, userId);
	}
};

/**
 * The grid's saved drag order, read straight from the per-user store the grid itself uses. The emailed
 * report mirrors the grid's order through `onColumnOrderChange`, but the grid only reports a saved order
 * when the user id arrives AFTER mount — with the id already known it never fires, so a reload would
 * email catalog order. Seeding from the store closes that gap; the migration runs first so a layout still
 * under the previous key is seen in its migrated form. No user id yet means nothing to read; the grid
 * reports the order itself once the id arrives.
 */
export const readSavedGridColumnOrder = (userId: string): string[] => {
	// DEFAULT: an empty order is the grid's own "not customized" value (what its reset writes), not a stand-in.
	if (!userId) return [];
	migrateLegacyMultiFacilityGridStorage(userId);
	return localStorageService.getTableColumnOrder(managementCenterMultiGridKey, userId) ?? [];
};
